/*
 * NOVDAT — Adaptive sticky header.
 * Watches [data-header-theme] elements as they pass behind the sticky
 * header and toggles a class so the header always shows the opposite
 * tone of whatever is currently behind it (light background -> dark
 * header, dark background -> light header).
 *
 * Some sections (e.g. the hero banner) wrap a differently-colored inner
 * box inside a lighter padded outer section — both the outer wrapper and
 * the inner box can carry their own [data-header-theme], and whichever
 * one is the smallest (most tightly-scoped, i.e. most deeply nested)
 * currently-intersecting element wins, so the header still reads
 * correctly while passing over the outer padding.
 *
 * T11: pickTheme() used to break ties by DOM containment only (new
 * element wins if it's a descendant of the current pick) — fine for the
 * intentional nested case above, but for SIBLING sections (e.g. a tall
 * PDP content block followed by the footer) it meant whichever element's
 * "intersecting" IntersectionObserver entry arrived first just stuck
 * forever, since a later sibling can never be a descendant of an earlier
 * one. IntersectionObserver callbacks aren't guaranteed to arrive in
 * strict scroll order relative to each other, so a fast scroll could
 * report the footer "entering" before the previous section's "leaving"
 * was processed, permanently wedging the header on the wrong tone once
 * scrolling stopped. Fixed by re-verifying each candidate's real,
 * current position (getBoundingClientRect) at decision time instead of
 * trusting the Set's membership/insertion order — a candidate that has
 * actually scrolled off is dropped on the spot, so a stale entry can
 * never outrank the section genuinely behind the header right now.
 *
 * T12: also missing `root` on the IntersectionObserver -- Horizon's
 * squeeze desktop layout (>=990px) scrolls `.page-wrapper` internally
 * instead of the document (see @theme/scroll-container), and this
 * observer was never told that, so it watched the (non-scrolling)
 * document viewport instead of the real scroll container and never
 * meaningfully fired on desktop. Native sections/header.liquid's own
 * header.js already gets this right via getIntersectionRoot() -- this
 * file just wasn't updated to use it.
 */
import { getIntersectionRoot } from '@theme/scroll-container';

(function () {
  var header = document.getElementById('header-component');
  var sections = Array.prototype.slice.call(document.querySelectorAll('[data-header-theme]'));

  if (!header || !sections.length) return;

  var observer = null;
  var currentTheme = null;
  var intersecting = new Set();

  function setTheme(theme) {
    if (theme === currentTheme) return;
    currentTheme = theme;
    header.classList.toggle('header--adaptive-inverted', theme === 'light');
  }

  function pickTheme(bandY) {
    if (!intersecting.size) return null;

    var winner = null;
    var winnerHeight = Infinity;

    intersecting.forEach(function (el) {
      var rect = el.getBoundingClientRect();

      // Drop candidates that no longer genuinely overlap the band behind
      // the header -- guards against stale/out-of-order IntersectionObserver
      // entries so a sibling that has actually scrolled away can never
      // keep winning over the section that's really there now.
      if (rect.bottom < bandY || rect.top > bandY) {
        intersecting.delete(el);
        return;
      }

      // Among genuinely-overlapping candidates, prefer the smallest one --
      // implements "most deeply nested wins" for the intentional
      // outer-wrapper + inner-box case without relying on DOM containment.
      var height = rect.bottom - rect.top;
      if (height < winnerHeight) {
        winner = el;
        winnerHeight = height;
      }
    });

    return winner ? winner.getAttribute('data-header-theme') : null;
  }

  function headerHeight() {
    var value = getComputedStyle(document.body).getPropertyValue('--header-height');
    var parsed = parseFloat(value);
    return isNaN(parsed) ? 60 : parsed;
  }

  function buildObserver() {
    if (observer) observer.disconnect();
    intersecting.clear();

    var topOffset = headerHeight();
    var bottomOffset = Math.max(window.innerHeight - topOffset - 1, 0);

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            intersecting.add(entry.target);
          } else {
            intersecting.delete(entry.target);
          }
        });

        var theme = pickTheme(topOffset);
        if (theme) setTheme(theme);
      },
      {
        root: getIntersectionRoot(),
        rootMargin: '-' + topOffset + 'px 0px -' + bottomOffset + 'px 0px',
        threshold: 0,
      }
    );

    sections.forEach(function (section) {
      observer.observe(section);
    });
  }

  buildObserver();
  window.addEventListener('load', buildObserver);

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(buildObserver, 200);
  });
})();
